import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// =============================================================================
// Lightweight i18n â€” hand-rolled, no dependency. Cukup untuk scope sidebar +
// topbar + label umum. Default = Indonesia. Toggle via LangSwitcher di topbar.
//
// Cara pakai:
//   const t = useT();
//   <span>{t('sidebar.monitoring')}</span>
//
// Tambah string baru: append ke TRANSLATIONS.id dan .en (key sama).
// Kalau key missing di bahasa aktif, fallback ke id, fallback ke key literal.
// =============================================================================

export type Locale = 'id' | 'en';

const TRANSLATIONS: Record<Locale, Record<string, string>> = {
  id: {
    // Sidebar
    'sidebar.dashboard':            'Dashboard',
    'sidebar.monitoring':           'Monitoring',
    'sidebar.playback':             'Playback',
    'sidebar.tripReport':           'Trip Report',
    'sidebar.report':               'Report',
    'sidebar.report.playback':      'Playback',
    'sidebar.report.temperature':   'Temperature',
    'sidebar.report.stopPark':      'Stop/Parking',
    'sidebar.report.events':        'Events',
    'sidebar.report.unileverShipment': 'Unilever Shipment',
    'sidebar.vehicles':             'Kendaraan',
    'sidebar.users':                'User',
    'sidebar.deviceMember':         'Link Device',
    'sidebar.maintenance':          'Maintenance',
    'sidebar.maintenance.schedule': 'Input Jadwal Service',
    'sidebar.maintenance.records':  'Rekaman Service',
    'sidebar.rental':               'Rental',
    'sidebar.rental.input':         'Input Sewa',
    'sidebar.rental.return':        'Pengembalian',
    'sidebar.rental.report':        'Laporan',
    'sidebar.rental.checklist':     'Master Checklist',
    'sidebar.invoice':              'Invoice',
    'sidebar.invoice.cost':         'Setting Biaya Sewa',
    'sidebar.invoice.generate':     'Generate Invoice',
    'sidebar.integration':          'Integration',
    'sidebar.integration.unileverSetting':  'Setting Unilever',
    'sidebar.integration.unileverShipment': 'Shipment Unilever',
    'sidebar.integration.stanley':          'Stanley',
    'sidebar.apiKeys':              'API Keys',
    'sidebar.vehicleBrand':         'Vehicle Brand',
    'sidebar.gpsBrand':             'GPS Brand & Model',
    'sidebar.platformProfile':      'Platform Profile',
    'sidebar.whatsappSetup':        'WhatsApp Session',
    'sidebar.tenants':              'Tenant',
    'sidebar.events':               'Events',
    'sidebar.activeShares':         'Share Aktif',
    // Topbar
    'topbar.notifications':         'Notifikasi',
    'topbar.profile':               'Edit Profile',
    'topbar.logout':                'Logout',
    'topbar.switchUser':            'Switch User',
    'topbar.viewing':               'Lihat data',
    'topbar.theme':                 'Tema',
    'topbar.theme.light':           'Terang',
    'topbar.theme.dark':            'Gelap',
    'topbar.theme.system':          'Sistem',
    'topbar.language':              'Bahasa',
    // Common
    'common.save':       'Simpan',
    'common.cancel':     'Batal',
    'common.delete':     'Hapus',
    'common.edit':       'Edit',
    'common.add':        'Tambah',
    'common.refresh':    'Refresh',
    'common.search':     'Cari...',
    'common.loading':    'Memuat...',
    'common.empty':      'Tidak ada data.',
    'common.confirm.delete': 'Yakin ingin menghapus?',
  },
  en: {
    'sidebar.dashboard':            'Dashboard',
    'sidebar.monitoring':           'Monitoring',
    'sidebar.playback':             'Playback',
    'sidebar.tripReport':           'Trip Report',
    'sidebar.vehicles':             'Vehicles',
    'sidebar.users':                'Users',
    'sidebar.deviceMember':         'Device Members',
    'sidebar.maintenance':          'Maintenance',
    'sidebar.maintenance.schedule': 'Service Schedule',
    'sidebar.maintenance.records':  'Service Records',
    'sidebar.rental':               'Rental',
    'sidebar.rental.input':         'New Rental',
    'sidebar.rental.return':        'Return',
    'sidebar.rental.report':        'Report',
    'sidebar.rental.checklist':     'Master Checklist',
    'sidebar.invoice':              'Invoice',
    'sidebar.invoice.cost':         'Rental Cost Setting',
    'sidebar.invoice.generate':     'Generate Invoice',
    'sidebar.integration':          'Integration',
    'sidebar.integration.unileverSetting':  'Unilever Setting',
    'sidebar.integration.unileverShipment': 'Unilever Shipment',
    'sidebar.integration.stanley':          'Stanley',
    'sidebar.apiKeys':              'API Keys',
    'sidebar.vehicleBrand':         'Vehicle Brand',
    'sidebar.gpsBrand':             'GPS Brand & Model',
    'sidebar.platformProfile':      'Platform Profile',
    'sidebar.whatsappSetup':        'WhatsApp Session',
    'sidebar.tenants':              'Tenants',
    'sidebar.events':               'Events',
    'sidebar.activeShares':         'Active Shares',
    'topbar.notifications':         'Notifications',
    'topbar.profile':               'Edit Profile',
    'topbar.logout':                'Logout',
    'topbar.switchUser':            'Switch User',
    'topbar.viewing':               'Viewing',
    'topbar.theme':                 'Theme',
    'topbar.theme.light':           'Light',
    'topbar.theme.dark':            'Dark',
    'topbar.theme.system':          'System',
    'topbar.language':              'Language',
    'common.save':       'Save',
    'common.cancel':     'Cancel',
    'common.delete':     'Delete',
    'common.edit':       'Edit',
    'common.add':        'Add',
    'common.refresh':    'Refresh',
    'common.search':     'Search...',
    'common.loading':    'Loading...',
    'common.empty':      'No data.',
    'common.confirm.delete': 'Confirm delete?',
  },
};

interface LocaleState {
  locale: Locale;
  setLocale: (l: Locale) => void;
}

export const useLocaleStore = create<LocaleState>()(
  persist(
    (set) => ({
      locale: 'id', // default Indonesia
      setLocale: (l) => set({ locale: l }),
    }),
    { name: 'webgps-locale' },
  ),
);

// Translate function. Pakai di komponen:
//   const t = useT();
//   {t('sidebar.monitoring')}
export function useT() {
  const locale = useLocaleStore((s) => s.locale);
  return (key: string): string => {
    return TRANSLATIONS[locale][key] ?? TRANSLATIONS.id[key] ?? key;
  };
}

export const LOCALES: Array<{ value: Locale; label: string; flag: string }> = [
  { value: 'id', label: 'Indonesia', flag: 'ðŸ‡®ðŸ‡©' },
  { value: 'en', label: 'English',   flag: 'ðŸ‡¬ðŸ‡§' },
];
